import { SchedulerInterval } from '@jupiterone/jupiter-types';
import { Question } from '@jupiterone/jupiter-types/question';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Field } from 'formik';
import get from 'lodash.get';
import React, { ChangeEvent } from 'react';
import { HelpCircle as HelpIcon } from 'react-feather';
import { EntitlementsFeatureTooltip } from '@jupiterone/jupiter-web-toolkit/src/entitlements';
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit/src/theme/types';
import ComplianceFormFields from './ComplianceFormFields';
import FormTextField from './FormTextField';
import QueryFormFields from './QueryFormFields';
import QuestionChipInput from './QuestionChipInput';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import clsx from 'clsx';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    section: {
      marginTop: theme.spacing(6),
    },

    disabledForEntitlement: {
      cursor: 'default',
      color: theme.palette.primary.contrastText,
      opacity: 0.5,
    },
    trendFormControl: {
      marginBottom: theme.spacing(1),
      maxWidth: 400,
      color: theme.palette.primary.main,

      '&$disabled': {
        '& span': {
          color: theme.palette.primary.light,
          opacity: 0.7,
        },
      },
    },
  })
);

const SHOW_TREND_HELP = (
  <>
    Selecting this option enables daily polling on this question and presents a
    trend chart in the result view when the question runs.
    <br />
    <br /> Do not use `count()` function in the queries in this question because
    the daily polling counts the number of results (rows). Using `count()`
    results in a single count of 1.
  </>
);

interface Props {
  disabled?: boolean;
  onChange: (inputEvent: { target: { name: string; value: any } }) => void;
  question: Question;
  setFormValues: (question: Question) => void;
  complianceStandards: string[];
}

const QuestionModalFields = ({
  disabled,
  onChange,
  question,
  setFormValues,
  complianceStandards,
}: Props) => {
  const classes = useStyles();

  const handleChangeChip = (name: string) => (value: any) => {
    onChange({
      target: {
        name,
        value,
      },
    });
  };

  const onChecked = ({
    target: { name, checked },
  }: ChangeEvent<HTMLInputElement>) => {
    onChange({ target: { name, value: checked } });
    onChange({
      target: {
        name: 'pollingInterval',
        value: checked ? SchedulerInterval.ONE_DAY : SchedulerInterval.DISABLED,
      },
    });
  };

  return (
    <>
      <Field name="title">
        {({ field, form }: any) => (
          <FormTextField
            name={field.name}
            value={field.value}
            label="Title"
            disabled={disabled}
            onChange={onChange}
            errorText={
              get(form.touched, field.name) && get(form.errors, field.name)
            }
          />
        )}
      </Field>
      <Field name="description">
        {({ field, form }: any) => (
          <FormTextField
            name={field.name}
            value={field.value}
            label="Description"
            disabled={disabled}
            onChange={onChange}
            errorText={
              get(form.touched, field.name) && get(form.errors, field.name)
            }
          />
        )}
      </Field>
      <EntitlementsFeatureTooltip
        entitlement={FeatureEntitlementName.QUESTION_TREND_ANALYSIS}
      >
        {(hasEntitlement) => (
          <FormControlLabel
            className={clsx(
              hasEntitlement && classes.trendFormControl,
              !hasEntitlement && classes.disabledForEntitlement
            )}
            control={
              <Checkbox
                color="primary"
                checked={hasEntitlement ? question.showTrend : false}
                onChange={(e) => (hasEntitlement ? onChecked(e) : undefined)}
                className={clsx(
                  !hasEntitlement && classes.disabledForEntitlement
                )}
                name="showTrend"
              />
            }
            label={
              <>
                Enable daily trend data collection
                {hasEntitlement && (
                  <Tooltip title={SHOW_TREND_HELP} placement="bottom">
                    <IconButton>
                      <HelpIcon size={20} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            }
          />
        )}
      </EntitlementsFeatureTooltip>
      <QuestionChipInput
        label="Tags"
        defaultValue={question.tags}
        disabled={disabled}
        onChange={handleChangeChip('tags')}
      />
      <div className={classes.section}>
        <QueryFormFields
          disabled={disabled}
          question={question}
          onChange={onChange}
          setFormValues={setFormValues}
        />
      </div>
      {complianceStandards.length > 0 && (
        <div className={classes.section}>
          <ComplianceFormFields
            disabled={disabled}
            question={question}
            onChange={onChange}
            setFormValues={setFormValues}
            complianceStandards={complianceStandards}
            onChangeChip={handleChangeChip}
          />
        </div>
      )}
    </>
  );
};

export default QuestionModalFields;
